/**
 * Editor for a {type:'name'} condition — bank-name-rule semantics
 * (case-insensitive, ANY word matches), reusing the shared operator labels
 * and the one-input-per-word list from bank name rules.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { BANK_RULE_OPERATORS, type BankRuleOperator } from '@trafficpulse/shared';

import { Button } from '@/components/ui';
import { RuleWordList } from '@/features/bank-name-rules/components/dialogs';

import type { ListGroupCondition } from '../../list-groups.types';

interface Props {
  initial?: { operator: BankRuleOperator; terms: string[] };
  onApply: (condition: ListGroupCondition) => void;
}

export const NameConditionEditor: FC<Props> = ({ initial, onApply }) => {
  const [operator, setOperator] = useState<BankRuleOperator>(
    initial?.operator ?? 'contains',
  );
  const [words, setWords] = useState<string[]>(
    initial?.terms.length ? initial.terms : [''],
  );
  const terms = words.map((w) => w.trim()).filter(Boolean);

  return (
    <div className="flex w-72 flex-col gap-2 p-2">
      <label className="flex flex-col gap-1 text-xs text-muted-foreground">
        When the list name…
        <select
          value={operator}
          onChange={(e) => setOperator(e.target.value as BankRuleOperator)}
          className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
        >
          {BANK_RULE_OPERATORS.map((op) => (
            <option key={op.value} value={op.value}>
              {op.label}
            </option>
          ))}
        </select>
      </label>
      <div className="text-xs text-muted-foreground">…any of these words (case doesn’t matter)</div>
      <RuleWordList words={words} onChange={setWords} />
      <Button
        size="sm"
        disabled={terms.length === 0}
        onClick={() => onApply({ type: 'name', operator, terms })}
      >
        Apply
      </Button>
    </div>
  );
};
